import { useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { JobService } from "@/services/job.service";
import type { Job, ContractorOpportunity } from "@/data/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { CheckCircle, DollarSign, Star, Trophy, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import { assertContractorAssignable } from "@/data/payProfileGuard";
import { assertJobKitReady } from "@/data/kitDispatchGuard";
import { getKit } from "@/data/materialKit";
import {
  canMarkJobReady,
  isExecutionReady,
  EXECUTION_LOCK_BLOCK_TITLE,
  getExecutionLockBlockDescription,
  getExecutionLockToastOptions,
} from "@/data/executionReadyLock";
import {
  assertMaterialAlignment,
  describeAlignmentBlock,
} from "@/data/materialAlignmentGate";
import { useVendorPO } from "@/data/VendorPOContext";
import { useOrderDraft } from "@/data/OrderDraftContext";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  opportunities: ContractorOpportunity[];
}

function getJobDisplayRef(job: Job): string {
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) return job.jobNumber.trim();
  return job.id;
}

export function SelectWinnerDialog({ open, onOpenChange, job, opportunities: jobOpps }: Props) {
  const { contractors, setJobs, setOpportunities, payProfiles, materialKits, buildReviewEvents } = useAppData();
  const { purchaseOrders } = useVendorPO();
  const { drafts } = useOrderDraft();
  const [selectedOpp, setSelectedOpp] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const biddable = jobOpps.filter(
    (o) => o.responseStatus === "Bid Submitted" || o.responseStatus === "Accepted"
  );

  const handleConfirm = () => {
    const opp = biddable.find((o) => o.id === selectedOpp);
    if (!opp) return;
    const contractor = contractors.find((c) => c.id === opp.contractorId);
    if (!contractor) return;

    // ── Execution Ready lock (checked FIRST — cheapest, broadest gate) ──
    if (!isExecutionReady(job)) {
      const readinessVerdict = canMarkJobReady({
        job,
        kit: getKit(materialKits, job.id),
        payProfiles,
        contractors,
        buildReviewEvents,
      });
      toast.warning(
        EXECUTION_LOCK_BLOCK_TITLE,
        getExecutionLockToastOptions(getExecutionLockBlockDescription(readinessVerdict)),
      );
    }

    // ── Material Alignment gate (spec === po === kit) ──
    // Runs before pay/kit guards: if the materials disagree, no other gate
    // matters — the truck would roll on a mismatched kit.
    const alignment = assertMaterialAlignment(job, {
      kit: getKit(materialKits, job.id),
      orderDrafts: drafts,
      purchaseOrders,
    });
    if (!alignment.ok) {
      const copy = describeAlignmentBlock(alignment);
      toast.error(copy.title, { description: copy.description });
      return;
    }

    // ── Pay Profile guard ──
    const guard = assertContractorAssignable({
      contractor, projectType: job.projectType, payProfiles,
    });
    if (!guard.allowed) {
      toast.error(guard.blockTitle, { description: guard.blockDescription });
      return;
    }

    // ── Material Kit dispatch guard ──
    const kitGuard = assertJobKitReady({ kit: getKit(materialKits, job.id) });
    if (!kitGuard.allowed) {
      toast.warning("Winner selected with readiness warning", { description: kitGuard.blockDescription });
    }

    const today = new Date().toISOString().split("T")[0];

    // Mark winner
    setOpportunities((prev) =>
      prev.map((o) => {
        if (o.id === opp.id) return { ...o, responseStatus: "Accepted" as const, isWinner: true };
        if (o.jobId === job.id && o.id !== opp.id) return { ...o, isWinner: false };
        return o;
      })
    );

    // Update job — set to Awaiting Response (pending contractor acceptance)
    setJobs((prev) =>
      prev.map((j) =>
        j.id === job.id
          ? {
              ...j,
              assignedSubcontractor: contractor.companyName,
              assignedCrew: contractor.companyName,
              crewId: undefined,
              assignmentType: "Subcontractor" as const,
              dispatchStatus: "Awaiting Response" as const,
              contractorAssignmentStatus: "Pending Acceptance",
              currentStage: "Available for Contractor Assignment" as const,
              activityHistory: [
                ...j.activityHistory,
                {
                  id: `act-${Date.now()}`,
                  date: today,
                  action: `${contractor.companyName} selected as winning contractor${opp.bidAmount ? ` ($${opp.bidAmount.toLocaleString()})` : ""} — awaiting acceptance`,
                  user: "Admin",
                  noteType: "ops" as const,
                },
              ],
            }
          : j
      )
    );

    JobService.updateStage(job.id, {
      currentStage: "Available for Contractor Assignment",
      dispatchStatus: "Awaiting Response",
      contractorAssignmentStatus: "Pending Acceptance",
      assignedSubcontractor: contractor.companyName,
      crewId: undefined,
      assignmentType: "Subcontractor",
    });

    setConfirmed(true);
    setTimeout(() => {
      setConfirmed(false);
      setSelectedOpp(null);
      onOpenChange(false);
    }, 1500);
  };

  const lowestBid = biddable.filter((o) => o.bidAmount).length > 0
    ? Math.min(...biddable.filter((o) => o.bidAmount).map((o) => o.bidAmount!))
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Select Winning Contractor</DialogTitle>
          <DialogDescription className="text-xs">
            {getJobDisplayRef(job)} · {job.projectType} · Payout ${job.payoutAmount.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <Trophy className="h-10 w-10 text-success" />
            <p className="text-sm font-semibold">Contractor Assigned!</p>
          </div>
        ) : biddable.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center">
            No bids or acceptances to select from yet.
          </p>
        ) : (
          <>
            {/* Summary of all responses */}
            <div className="flex items-center gap-4 text-[10px] text-muted-foreground border-b pb-2 mb-2">
              <span>{jobOpps.length} contacted</span>
              <span>{jobOpps.filter((o) => o.viewedDate).length} viewed</span>
              <span className="text-info font-medium">{biddable.length} responded</span>
              <span className="text-destructive">{jobOpps.filter((o) => o.responseStatus === "Rejected").length} declined</span>
            </div>

            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {biddable.map((opp) => {
                const con = contractors.find((c) => c.id === opp.contractorId);
                const isLowest = lowestBid !== null && opp.bidAmount === lowestBid;
                const isSelected = selectedOpp === opp.id;
                return (
                  <button
                    key={opp.id}
                    onClick={() => setSelectedOpp(opp.id)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      isSelected
                        ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                        : "border-border hover:bg-muted/30"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isSelected && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
                        <span className="text-sm font-medium">{con?.companyName || opp.contractorId}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge type="contractorResponse" value={opp.responseStatus} />
                        {isLowest && opp.bidAmount && (
                          <span className="text-[9px] bg-success/15 text-success rounded px-1.5 py-0.5 font-semibold uppercase">Lowest</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                      {opp.bidAmount && (
                        <span className="inline-flex items-center gap-1 font-semibold text-foreground">
                          <DollarSign className="h-3 w-3" />{opp.bidAmount.toLocaleString()}
                        </span>
                      )}
                      {con && (
                        <>
                          <span className="inline-flex items-center gap-0.5">
                            <Star className="h-2.5 w-2.5 text-warning fill-warning" />{con.rating} quality
                          </span>
                          <span className="inline-flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5 text-info" />{con.responsivenessRating} speed
                          </span>
                        </>
                      )}
                      {opp.viewedDate && (
                        <span className="inline-flex items-center gap-0.5">
                          <Eye className="h-2.5 w-2.5" /> Viewed {opp.viewedDate}
                        </span>
                      )}
                    </div>
                    {opp.bidNotes && (
                      <p className="text-[11px] text-muted-foreground mt-1.5 italic leading-relaxed">{opp.bidNotes}</p>
                    )}
                    {con && (!con.insuranceOnFile || !con.w9OnFile) && (
                      <p className="text-[10px] text-warning mt-1 font-medium">
                        ⚠ {!con.insuranceOnFile ? "Insurance missing" : ""}{!con.insuranceOnFile && !con.w9OnFile ? " · " : ""}{!con.w9OnFile ? "W-9 missing" : ""}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button size="sm" disabled={!selectedOpp} onClick={handleConfirm}>
                <Trophy className="h-3.5 w-3.5" /> Assign Winner
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
